import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, MoreHorizontal } from 'lucide-react';

interface MaintenanceModule {
    id: string;
    name: string;
    icon: React.ComponentType<any>;
    description: string;
    tableName: string;
    fields: string[];
}

interface SelectedRows {
    [key: string]: boolean;
}

interface MaintenanceDataTableProps {
    module: MaintenanceModule;
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
}

interface TableRow {
    id: string;
    [key: string]: any;
}

interface ColumnFilter {
    [key: string]: string;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

export const MaintenanceDataTable: React.FC<MaintenanceDataTableProps> = ({
    module,
    selectedRows,
    onRowSelection,
    onBulkSelection
}) => {
    const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
    const [globalFilter, setGlobalFilter] = useState<string>('');

    // Mock data - replace with actual API calls
    const generateMockData = (fields: string[]): TableRow[] => {
        const mockData: TableRow[] = [];
        for (let i = 1; i <= 50; i++) {
            const row: TableRow = { id: `${module.id}-${i}` };
            fields.forEach(field => {
                switch (field) {
                    case 'ItemNo':
                        row[field] = `ITEM${i.toString().padStart(4, '0')}`;
                        break;
                    case 'BinCode':
                        row[field] = `BIN${i.toString().padStart(3, '0')}`;
                        break;
                    case 'Description1':
                    case 'Description':
                        row[field] = `Beskrivelse for element ${i}`;
                        break;
                    case 'WarehouseID':
                        row[field] = `WH${(i % 3) + 1}`;
                        break;
                    case 'StcZoneID':
                        row[field] = `ZONE${(i % 5) + 1}`;
                        break;
                    case 'UnitOfMeasure':
                        row[field] = ['STK', 'KG', 'L', 'M'][i % 4];
                        break;
                    case 'ItemGroup':
                        row[field] = `GRUPPE${(i % 10) + 1}`;
                        break;
                    case 'BinTypeNo':
                    case 'BinGrpNo':
                    case 'MovementTypeNo':
                    case 'BCElementID':
                    case 'BarcodeID':
                        row[field] = Math.floor(Math.random() * 100) + 1;
                        break;
                    case 'PickPriorityNo':
                    case 'PutAwayPriorityNo':
                    case 'Priority':
                    case 'FromPriority':
                    case 'ToPriority':
                        row[field] = Math.floor(Math.random() * 10) + 1;
                        break;
                    case 'ActiveYesNo':
                        row[field] = Math.random() > 0.3 ? 'Ja' : 'Nei';
                        break;
                    case 'AI':
                        row[field] = `(${(i % 99) + 1})`;
                        break;
                    case 'Length':
                        row[field] = Math.floor(Math.random() * 20) + 1;
                        break;
                    default:
                        row[field] = `Verdi ${i}`;
                }
            });
            mockData.push(row);
        }
        return mockData;
    };

    const data = useMemo(() => generateMockData(module.fields), [module]);

    // Filter data based on column filters and global search
    const filteredData = useMemo(() => {
        let filtered = data;

        // Apply global filter
        if (globalFilter) {
            filtered = filtered.filter(row => 
                Object.values(row).some(value => 
                    value.toString().toLowerCase().includes(globalFilter.toLowerCase())
                )
            );
        }

        // Apply column filters
        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row =>
                    row[column]?.toString().toLowerCase().includes(filterValue.toLowerCase())
                );
            }
        });

        return filtered;
    }, [data, columnFilters, globalFilter]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleColumnFilter = (column: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const newSelection: SelectedRows = {};
        if (checked) {
            sortedData.forEach(row => {
                newSelection[row.id] = true;
            });
        }
        onBulkSelection(newSelection);
    };

    const isAllSelected = sortedData.length > 0 && sortedData.every(row => selectedRows[row.id]);
    const isIndeterminate = sortedData.some(row => selectedRows[row.id]) && !isAllSelected;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Table Header Controls */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900">{module.name}</h3>
                        <span className="text-sm text-gray-500">
                            ({sortedData.length} av {data.length} rader)
                        </span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Søk i alle kolonner..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <button className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                            <Filter className="w-4 h-4" />
                            <span className="text-sm">Filter</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            {/* Select All Checkbox */}
                            <th className="w-12 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isIndeterminate;
                                    }}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </th>
                            
                            {/* Column Headers */}
                            {module.fields.map(field => (
                                <th key={field} className="px-4 py-3 text-left">
                                    <div className="space-y-2">
                                        {/* Sort Header */}
                                        <button
                                            onClick={() => handleSort(field)}
                                            className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                        >
                                            <span>{field}</span>
                                            {sortConfig.key === field ? (
                                                sortConfig.direction === 'asc' ? (
                                                    <ChevronUp className="w-3 h-3" />
                                                ) : (
                                                    <ChevronDown className="w-3 h-3" />
                                                )
                                            ) : (
                                                <div className="w-3 h-3" />
                                            )}
                                        </button>
                                        
                                        {/* Column Filter */}
                                        <input
                                            type="text"
                                            placeholder={`Filter ${field}...`}
                                            value={columnFilters[field] || ''}
                                            onChange={(e) => handleColumnFilter(field, e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </th>
                            ))}
                            
                            {/* Actions Column */}
                            <th className="w-16 px-4 py-3">
                                <span className="text-xs font-medium text-gray-700">Handlinger</span>
                            </th>
                        </tr>
                    </thead>
                    
                    <tbody className="divide-y divide-gray-200">
                        {sortedData.map(row => (
                            <tr 
                                key={row.id} 
                                className={`hover:bg-gray-50 transition-colors ${
                                    selectedRows[row.id] ? 'bg-blue-50' : ''
                                }`}
                            >
                                {/* Row Checkbox */}
                                <td className="px-4 py-3">
                                    <input
                                        type="checkbox"
                                        checked={selectedRows[row.id] || false}
                                        onChange={(e) => onRowSelection(row.id, e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                </td>
                                
                                {/* Data Columns */}
                                {module.fields.map(field => (
                                    <td key={field} className="px-4 py-3 text-sm text-gray-900">
                                        {row[field]}
                                    </td>
                                ))}
                                
                                {/* Actions */}
                                <td className="px-4 py-3">
                                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                        <MoreHorizontal className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Table Footer */}
            {sortedData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <p>Ingen data funnet med gjeldende filtre</p>
                </div>
            ) : (
                <div className="p-4 border-t border-gray-200 text-sm text-gray-600">
                    Viser {sortedData.length} av {data.length} rader
                    {Object.values(selectedRows).filter(Boolean).length > 0 && (
                        <span className="ml-4">
                            • {Object.values(selectedRows).filter(Boolean).length} rader valgt
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
